import { useState } from 'react';

import { useLingui } from '@lingui/react/macro';

interface UseEditableJsonOptions<T> {
  data: T | null;
  onSave: (data: T) => void;
  onClear?: () => void;
  validate?: (data: unknown) => data is T;
}

interface UseEditableJsonReturn<T> {
  // State
  isEditMode: boolean;
  jsonInput: string;
  error: string | null;

  // Actions
  enterEditMode: (data: T | null) => void;
  cancelEdit: () => void;
  applyEdit: () => void;
  clear: () => void;
  setJsonInput: (value: string) => void;
  setError: (error: string | null) => void;
  handleJsonEdit: (edit: { updated_src: T }) => void;
}

/**
 * Hook to manage editable JSON state and actions
 * Handles switching between view mode (JsonViewer) and edit mode (textarea)
 */
export function useEditableJson<T extends object>({
  data,
  onSave,
  onClear,
  validate,
}: UseEditableJsonOptions<T>): UseEditableJsonReturn<T> {
  const { t } = useLingui();
  const [isEditMode, setIsEditMode] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const enterEditMode = (currentData: T | null) => {
    if (currentData) {
      setJsonInput(JSON.stringify(currentData, null, 2));
    }
    setIsEditMode(true);
    setError(null);
  };

  const cancelEdit = () => {
    setIsEditMode(false);
    setError(null);
    setJsonInput('');
  };

  const applyEdit = () => {
    setError(null);

    if (!jsonInput.trim()) {
      setError(t`Please paste JSON data`);
      return;
    }

    try {
      const parsed = JSON.parse(jsonInput);

      // Use custom validator if provided, otherwise basic validation
      if (validate) {
        if (!validate(parsed)) {
          throw new Error(t`Invalid JSON structure`);
        }
      } else {
        // Basic validation
        if (
          typeof parsed !== 'object' ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          throw new Error(t`Invalid JSON structure`);
        }
      }

      onSave(parsed as T);
      setJsonInput('');
      setIsEditMode(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t`Invalid JSON. Please check your input.`,
      );
    }
  };

  const clear = () => {
    if (onClear) {
      onClear();
    }
    setJsonInput('');
    setError(null);
    setIsEditMode(false);
  };

  const handleJsonEdit = (edit: { updated_src: T }) => {
    onSave(edit.updated_src);
  };

  return {
    isEditMode,
    jsonInput,
    error,
    enterEditMode,
    cancelEdit,
    applyEdit,
    clear,
    setJsonInput,
    setError,
    handleJsonEdit,
  };
}
